/**
 * Solution health — the structural checks every Pypestream solution should
 * pass, from the conventions in the building guide
 * (`server-only/ai/solution-building-guide.ts`), as plain code: instant,
 * free, and the same answer every time. Ask AI covers what needs judgment;
 * this covers what can be counted.
 *
 * Pure: rows in, issues out. Fixes are described here and applied by the
 * store (`tableStore.applyHealthFix`), so the rules can be tested without
 * one.
 */

export type HealthSeverity = 'error' | 'warning' | 'info';

export type HealthFix =
  | { kind: 'add-error-handler'; errorTarget?: string }
  | { kind: 'add-error-route'; rowId: string; errorTarget: string };

export interface HealthIssue {
  /** Stable per rule and subject, so a list can key on it. */
  id: string;
  rule: HealthRule;
  severity: HealthSeverity;
  title: string;
  detail: string;
  /** Rows the issue is about, to jump to. */
  rows: { rowId: string; nodeNumber: string; name: string }[];
  fix?: HealthFix;
}

export type HealthRule =
  | 'no-error-handler'
  | 'reserved-command'
  | 'broken-route'
  | 'no-error-route'
  | 'missing-script'
  | 'empty-node'
  | 'dead-end';

export interface HealthRow {
  id: string;
  nodeNumber?: unknown;
  nodeType?: unknown;
  nodeName?: unknown;
  message?: unknown;
  command?: unknown;
  nextNodes?: unknown;
  whatsNext?: unknown;
  answerRequired?: unknown;
  behaviors?: unknown;
  richAssetType?: unknown;
}

export const ERROR_HANDLER_NODE = '-500';

/** Commands the platform only allows on one reserved node. */
const RESERVED_COMMANDS: Record<string, string> = {
  HandleBotError: ERROR_HANDLER_NODE,
};

const text = (v: unknown) => (typeof v === 'string' ? v.trim() : '');

const list = (v: unknown): string[] =>
  (Array.isArray(v)
    ? v.map((x) => String(x ?? ''))
    : typeof v === 'string'
      ? v.split(/[|,;]/)
      : []
  )
    .map((s) => s.trim())
    .filter(Boolean);

/** A node number as the solution writes it: "-500", "7020". */
export const nodeKey = (v: unknown): string => {
  const raw = String(v ?? '')
    .replace(/^'/, '')
    .trim();
  // Blank is no destination at all, not node 0.
  if (!raw) return '';
  const n = Number(raw);
  return Number.isFinite(n) ? String(n) : '';
};

const isAction = (row: HealthRow) => text(row.nodeType).toLowerCase() === 'a';
const isDecision = (row: HealthRow) => text(row.nodeType).toLowerCase() === 'd';

/** `outcome~node` items, split. */
const routes = (row: HealthRow) =>
  list(row.whatsNext).map((item) => {
    const [outcome, dest = ''] = item.split('~');
    return { outcome: outcome.trim().toLowerCase(), dest: nodeKey(dest) };
  });

const ref = (row: HealthRow) => ({
  rowId: row.id,
  nodeNumber: nodeKey(row.nodeNumber),
  name: text(row.nodeName),
});

/**
 * Where this solution already sends errors: the destination most action
 * nodes' `error` outcome uses. That's the target a fix routes to, so a fix
 * follows the solution's own convention instead of inventing one.
 */
export const commonErrorTarget = (rows: HealthRow[]): string | undefined => {
  const counts = new Map<string, number>();
  rows.forEach((row) =>
    routes(row).forEach((r) => {
      if (r.outcome === 'error' && r.dest) {
        counts.set(r.dest, (counts.get(r.dest) ?? 0) + 1);
      }
    })
  );
  return [...counts.entries()].sort((a, b) => b[1] - a[1])[0]?.[0];
};

export const checkSolutionHealth = (
  rows: HealthRow[],
  {
    nativeCommands,
    uploadedScripts,
  }: { nativeCommands: readonly string[]; uploadedScripts: readonly string[] }
): HealthIssue[] => {
  const issues: HealthIssue[] = [];
  const numbers = new Set(rows.map((r) => nodeKey(r.nodeNumber)));
  const native = new Set(nativeCommands);
  const scripts = new Set(uploadedScripts);
  const errorTarget = commonErrorTarget(rows);

  if (!numbers.has(ERROR_HANDLER_NODE)) {
    issues.push({
      id: 'no-error-handler',
      rule: 'no-error-handler',
      severity: 'error',
      title: 'No error handler (#-500)',
      detail:
        'When an action fails or times out, customers get the platform’s generic “technical difficulties” message instead of a recovery path.',
      rows: [],
      fix: { kind: 'add-error-handler', errorTarget },
    });
  }

  const missingScripts = new Map<string, HealthRow[]>();
  rows.forEach((row) => {
    const num = nodeKey(row.nodeNumber);
    const command = text(row.command);

    const reservedFor = RESERVED_COMMANDS[command];
    if (reservedFor && num !== reservedFor) {
      issues.push({
        id: `reserved-command:${row.id}`,
        rule: 'reserved-command',
        severity: 'error',
        title: `${command} on #${num}`,
        detail: `${command} only runs on the error handler node #${reservedFor}; the platform rejects it anywhere else.`,
        rows: [ref(row)],
      });
    }

    const broken = [
      ...list(row.nextNodes).map(nodeKey),
      ...routes(row).map((r) => r.dest),
    ].filter((dest) => dest && !numbers.has(dest));
    if (broken.length) {
      const uniq = [...new Set(broken)];
      issues.push({
        id: `broken-route:${row.id}`,
        rule: 'broken-route',
        severity: 'error',
        title: `#${num} routes to ${uniq.map((n) => `#${n}`).join(', ')}, which ${uniq.length === 1 ? "doesn't" : "don't"} exist`,
        detail: 'Customers who reach that route hit an error.',
        rows: [ref(row)],
      });
    }

    if (isAction(row) && command) {
      const outs = routes(row);
      // The error handler branches on error_type; it IS where errors go.
      if (
        num !== ERROR_HANDLER_NODE &&
        outs.length &&
        !outs.some((r) => r.outcome === 'error')
      ) {
        issues.push({
          id: `no-error-route:${row.id}`,
          rule: 'no-error-route',
          severity: 'warning',
          title: `#${num} has no error route`,
          detail: errorTarget
            ? `If ${command} fails, the chat has nowhere to go. Most of this solution sends errors to #${errorTarget}.`
            : `If ${command} fails, the chat has nowhere to go.`,
          rows: [ref(row)],
          ...(errorTarget && errorTarget !== num
            ? {
                fix: {
                  kind: 'add-error-route' as const,
                  rowId: row.id,
                  errorTarget,
                },
              }
            : {}),
        });
      }
      if (!native.has(command) && !scripts.has(`${command}.py`)) {
        missingScripts.set(command, [
          ...(missingScripts.get(command) ?? []),
          row,
        ]);
      }
    }

    const hasNext = list(row.nextNodes).length > 0 || routes(row).length > 0;
    if (isAction(row) && !command) {
      issues.push({
        id: `empty-node:${row.id}`,
        rule: 'empty-node',
        severity: 'warning',
        title: `#${num} is an action node with no command`,
        detail: 'It runs nothing, so it does nothing.',
        rows: [ref(row)],
      });
    } else if (
      isDecision(row) &&
      !text(row.message) &&
      !text(row.richAssetType) &&
      !hasNext
    ) {
      issues.push({
        id: `empty-node:${row.id}`,
        rule: 'empty-node',
        severity: 'warning',
        title: `#${num} is empty`,
        detail: 'No message, no options and nowhere to go.',
        rows: [ref(row)],
      });
    } else if (
      isDecision(row) &&
      text(row.message) &&
      !hasNext &&
      !text(row.richAssetType) &&
      !['true', '1'].includes(String(row.answerRequired).toLowerCase()) &&
      !list(row.behaviors).includes('xfer_to_agent')
    ) {
      issues.push({
        id: `dead-end:${row.id}`,
        rule: 'dead-end',
        severity: 'info',
        title: `The conversation stops at #${num}`,
        detail:
          'It shows a message and goes nowhere — fine for a goodbye, a gap anywhere else.',
        rows: [ref(row)],
      });
    }
  });

  missingScripts.forEach((usedBy, command) => {
    issues.push({
      id: `missing-script:${command}`,
      rule: 'missing-script',
      severity: 'error',
      title: `No script for ${command}`,
      detail: `${usedBy.length} node${usedBy.length === 1 ? '' : 's'} run${usedBy.length === 1 ? 's' : ''} it; upload ${command}.py in Actions, or the node errors when it runs.`,
      rows: usedBy.map(ref),
    });
  });

  const order: Record<HealthSeverity, number> = {
    error: 0,
    warning: 1,
    info: 2,
  };
  return issues.sort((a, b) => order[a.severity] - order[b.severity]);
};
